import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ArrowRight, Play, TrendingUp, Users, Database, Zap, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeroSection = () => {
  const [heroData, setHeroData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHeroData();
  }, []);

  const fetchHeroData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/hero-data`);
      setHeroData(response.data);
    } catch (error) {
      console.error('Error fetching hero data:', error);
      // Fallback to default data if API fails - using exact values from Infrastructure Dashboard
      setHeroData({
        title: "Gujarat Women & Child Development Department",
        subtitle: "Empowering Women & Ensuring Child Development Through AI-Driven Governance",
        description: "Leading comprehensive development programs for women empowerment and child nutrition in Gujarat through innovative technology and data-driven decision making.",
        stats: [
          { label: "Anganwadi Centers", value: "53,065" },
          { label: "Districts Covered", value: "41" },
          { label: "Women Beneficiaries", value: "16.49L" },
          { label: "Children Served", value: "7.68L" }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExploreClick = () => {
    document.querySelector("#dashboard").scrollIntoView({ behavior: "smooth" });
  };

  const handleWatchDemoClick = () => {
    // Mock demo action
    alert("Demo video would play here!");
  };

  if (loading) {
    return (
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900" />
        <div className="relative z-10 text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-white" />
          <p className="text-white">Loading Gujarat WCD Department...</p>
        </div>
      </section>
    );
  }

  if (!heroData) {
    return null;
  }

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-pink-900" />
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-pink-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full">
          <div className="grid grid-cols-12 gap-4 opacity-10">
            {Array.from({ length: 144 }, (_, i) => (
              <div 
                key={i} 
                className="w-2 h-2 bg-white rounded-full animate-twinkle"
                style={{ animationDelay: `${Math.random() * 3}s` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="space-y-6 sm:space-y-8">
          {/* Main Heading */}
          <div className="space-y-4 sm:space-y-6">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-[1.2] sm:leading-[1.15] md:leading-tight px-2 sm:px-4 break-words">
              <span className="text-white">Gujarat </span>
              <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Women & Child </span>
              <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Development </span>
              <span className="text-white">Department</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed px-4 sm:px-6 animate-fade-in-up" style={{ animationDelay: "0.6s" }}>
              {heroData.subtitle}
            </p>
          </div>

          {/* Description */}
          <p className="text-base sm:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed px-4 animate-fade-in-up" style={{ animationDelay: "0.8s" }}>
            {heroData.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up" style={{ animationDelay: "1s" }}>
            <Button 
              size="lg" 
              onClick={handleExploreClick}
              className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold px-8 py-3 text-lg shadow-2xl hover:shadow-pink-500/25 transition-all duration-300 transform hover:scale-105"
            >
              Explore WCD Dashboard
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={handleWatchDemoClick}
              className="border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm font-semibold px-8 py-3 text-lg transition-all duration-300 transform hover:scale-105"
            >
              <Play className="mr-2 w-5 h-5" />
              Watch Demo
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-16 animate-fade-in-up" style={{ animationDelay: "1.2s" }}>
            {heroData.stats.map((stat, index) => (
              <div 
                key={index} 
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
              >
                <div className="flex items-center justify-center mb-3">
                  {index === 0 && <TrendingUp className="w-6 h-6 text-pink-400" />}
                  {index === 1 && <Users className="w-6 h-6 text-pink-400" />}
                  {index === 2 && <Database className="w-6 h-6 text-pink-400" />}
                  {index === 3 && <Zap className="w-6 h-6 text-pink-400" />}
                </div>
                <div className="text-2xl lg:text-3xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-300">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
